import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Inbox, Mail, Phone, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { Textarea } from '@/components/textarea';
import { Dialog } from '@/components/dialog';
import { EmptyState } from '@/components/empty-state';
import { Pager } from '@/components/pager';
import { PageHeader } from '@/components/page-header';
import { SkeletonList } from '@/components/skeleton';
import { DataList, DataListRow } from '@/components/data-list';
import { Menu } from '@/components/menu';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import { useFormError } from '@/lib/form-error';
import { useClients } from '@/lib/clients';
import {
  useRequests,
  useCreateRequest,
  useDeleteRequest,
  useUpdateRequestStatus,
} from '@/lib/requests';
import { formatDate, formatStatus } from '@/lib/format';
import type { ClientRequestData } from '@/types/api';

const TYPE_LABELS: Record<string, string> = {
  support: 'Support',
  quote: 'Quote / proposal',
  general: 'General',
};

const CHANNEL_LABELS: Record<string, string> = {
  portal: 'Portal',
  phone: 'Phone',
  email: 'Email',
};

export function RequestsIndex() {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const { data: paginated, isLoading } = useRequests({ status: status || undefined, page });
  const rows = paginated?.data;
  const meta = paginated?.meta;

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5">
      <PageHeader
        icon={Inbox}
        title="Requests"
        subtitle="Support, quote, and general requests — submitted from the portal or logged from a call or email."
        action={
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" /> New request
          </Button>
        }
      />

      <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="max-w-[200px]">
        <option value="">All statuses</option>
        <option value="open">Open</option>
        <option value="in_progress">In progress</option>
        <option value="resolved">Resolved</option>
        <option value="closed">Closed</option>
      </Select>

      {isLoading ? (
        <SkeletonList rows={5} />
      ) : !rows || rows.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="No requests"
          description="Client requests will show up here as they come in — or log one you took by phone or email."
          action={
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" /> New request
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          <DataList>
            {rows.map((req) => (
              <RequestRow key={req.id} req={req} />
            ))}
          </DataList>
          {meta && meta.last_page > 1 ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </div>
      )}

      <NewRequestDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

function RequestRow({ req }: { req: ClientRequestData }) {
  const update = useUpdateRequestStatus();
  const del = useDeleteRequest();
  const confirm = useConfirm();
  const toast = useToast();

  return (
    <DataListRow interactive={false} className="items-start">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-foreground">{req.subject}</span>
          <span className="shrink-0 rounded bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
            {TYPE_LABELS[req.type] ?? req.type}
          </span>
          {req.channel !== 'portal' ? <ChannelChip channel={req.channel} /> : null}
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          {req.client_id ? (
            <Link to={`/clients/${req.client_id}`} className="hover:text-[var(--color-foreground)]">
              {req.client_name}
            </Link>
          ) : null}
          {req.created_by_name ? <span> · {req.created_by_name}</span> : null}
          <span> · {formatDate(req.created_at)}</span>
        </div>
        <p className="mt-1.5 line-clamp-2 text-sm text-[var(--color-foreground-muted)]">{req.body}</p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Select
          value={req.status}
          onChange={async (e) => {
            await update.mutateAsync({ id: req.id, status: e.target.value });
            toast.success(`Marked ${formatStatus(e.target.value).toLowerCase()}`);
          }}
          className="w-36"
        >
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </Select>
        <Menu
          items={[
            {
              key: 'delete',
              label: 'Delete request',
              icon: Trash2,
              danger: true,
              onSelect: async () => {
                const ok = await confirm({
                  title: 'Delete this request?',
                  body: 'It leaves the queue and the client stops seeing it in their portal. Mark it closed instead if the work is simply finished.',
                  confirmLabel: 'Delete request',
                  tone: 'danger',
                });
                if (ok) {
                  await del.mutateAsync(req.id);
                  toast.success('Request deleted');
                }
              },
            },
          ]}
        />
      </div>
    </DataListRow>
  );
}

function ChannelChip({ channel }: { channel: string }) {
  const Icon = channel === 'phone' ? Phone : Mail;
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
      <Icon className="size-3" />
      {CHANNEL_LABELS[channel] ?? channel}
    </span>
  );
}

function NewRequestDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: clientsPage } = useClients();
  const clients = clientsPage?.data ?? [];
  const create = useCreateRequest();
  const toast = useToast();
  const { error, wrap } = useFormError();

  const [clientId, setClientId] = useState<number | null>(null);
  const [channel, setChannel] = useState('phone');
  const [type, setType] = useState('support');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [pending, setPending] = useState(false);

  const resolvedClientId = clientId ?? clients[0]?.id ?? null;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!resolvedClientId) return;
    setPending(true);
    const ok = await wrap(() =>
      create.mutateAsync({ client_id: resolvedClientId, type, subject, body, channel }),
    );
    setPending(false);
    if (ok) {
      toast.success('Request logged');
      setSubject('');
      setBody('');
      setType('support');
      setChannel('phone');
      setClientId(null);
      onClose();
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Log a request">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Client">
          <Select value={resolvedClientId ?? ''} onChange={(e) => setClientId(Number(e.target.value))}>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Came in via">
            <Select value={channel} onChange={(e) => setChannel(e.target.value)}>
              <option value="phone">Phone</option>
              <option value="email">Email</option>
              <option value="portal">Portal</option>
            </Select>
          </Field>
          <Field label="Type">
            <Select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="support">Support</option>
              <option value="quote">Quote / proposal</option>
              <option value="general">General</option>
            </Select>
          </Field>
        </div>
        <Field label="Subject">
          <Input
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Short summary"
          />
        </Field>
        <Field label="Details">
          <Textarea
            required
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="What did the client ask for?"
            className="min-h-[120px]"
          />
        </Field>
        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={pending || !resolvedClientId}>
            {pending ? 'Saving…' : 'Log request'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-[var(--color-foreground-muted)]">{label}</span>
      {children}
    </label>
  );
}
